import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { registerSlotNotification } from '@/services/bookingApi';
import { Bell, X } from 'lucide-react';

interface NotifyMeButtonProps {
  profileId: string;
  date: string;
  time: string;
  serviceIds?: string[];
  compact?: boolean;
}

export const NotifyMeButton: React.FC<NotifyMeButtonProps> = ({
  profileId,
  date,
  time,
  serviceIds = [],
  compact = false,
}) => {
  const { t } = useTranslation('booking');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await registerSlotNotification({
        profile_id: profileId,
        service_ids: serviceIds,
        date,
        time,
        email,
      });
      setSuccess(t('notifyMe.registered') || `Registered! Position #${result.position}`);
      setShowEmailInput(false);
      setEmail('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-sm text-green-600 font-medium flex items-center gap-1">
        <Bell className="w-4 h-4" />
        {success}
      </div>
    );
  }

  if (showEmailInput) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 w-64">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium text-amber-800">
            {t('notifyMe.title') || 'Get notified'}
          </h4>
          <button
            onClick={() => setShowEmailInput(false)}
            className="text-amber-600 hover:text-amber-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('notifyMe.emailPlaceholder') || 'your@email.com'}
            required
            className="w-full px-3 py-1.5 text-sm border border-amber-300 rounded-lg focus:ring-amber-500 focus:border-amber-500"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full px-3 py-1.5 bg-amber-500 text-white text-sm rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50"
          >
            {isLoading ? t('common.loading') : t('notifyMe.notifyMe') || 'Notify Me'}
          </button>
        </form>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={() => setShowEmailInput(true)}
        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
        title={t('notifyMe.button') || 'Notify me when available'}
      >
        <Bell className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      onClick={() => setShowEmailInput(true)}
      className="px-3 py-1.5 bg-amber-100 text-amber-700 border border-amber-300 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors flex items-center gap-1"
    >
      <Bell className="w-4 h-4" />
      {t('notifyMe.button') || 'Notify me'}
    </button>
  );
};
